// Assistant « Nouvelle étude », EN PLEINE PAGE — le TYPE d'abord.
//
// Un entretien, une observation et un questionnaire ne se préparent pas de la
// même façon : on choisit donc ce qu'on va faire AVANT de le cadrer.
//   1. le type d'étude ;
//   2. le cadrage : objectif, méthode, cible, participants, période, contexte
//      (+ ce qui est propre au type : personas d'un test, périmètre d'un audit) ;
//   3. le protocole propre au type : guide d'entretien, grille d'observation,
//      questions, scénario de test, ou périmètre d'audit ;
// Puis, pour les études qui ont des SÉANCES (entretiens, observations, tests) :
//   4. les participants, cochés dans l'annuaire (pool, persona, rôle…) ;
//   5. les créneaux (cochés, d'après l'agenda) et les formats proposés ;
//   6. le récapitulatif : créer l'étude ET envoyer les invitations.
// Avec `existing`, l'étude existe déjà : on ne fait que les étapes 4 à 6.
import { useState, type FormEvent } from 'react';
import { api, ApiRequestError } from '../api/client';
import { useCreateStudy } from '../api/queries';
import { useQueryClient } from '@tanstack/react-query';
import type { NewStudy, StudyKind, StudyProtocol } from '../api/types';
import { Button, buttonClass, ErrorBanner, Field, Note, Select } from '../components/ui';
import { PersonaPicker, TextArea } from '../write/Forms';
import { hrefFor, Link, useRouter } from '../router';
import { presetTab } from '../components/Tabs';
import { PeoplePicker } from '../people/PeoplePicker';
import { personName, takePresetPeople, usePeople } from '../people/api';
import { FORMAT_LABELS, FormatPicker, SlotPicker, type Format } from '../invitations/SlotPicker';
import { useMailStatus, type BatchResult } from '../invitations/api';
import { InvitationMessage } from '../invitations/InvitationMessage';
import { KINDS, KIND_BY_KEY, emptyProtocol, protocolSize, studyKind } from './kinds';
import { ProtocolEditor, TaskListEditor } from './ProtocolEditor';
import { DemoQuota } from '../demo/DemoUsage';
import './studies.css';

const STEP_LABELS = ['Type d’étude', 'Cadrage', 'Protocole', 'Participants', 'Créneaux et format', 'Récapitulatif'];
const DURATIONS = [15, 30, 45, 60, 90, 120];

/** Un audit ou un questionnaire n'a pas de séance : ni participants à inviter, ni créneaux. */
const hasSessions = (k: StudyKind | null) => Boolean(k) && k !== 'audit' && k !== 'questionnaire';

export function NewStudyWizard({
  projectId,
  onDone,
  presetKind = null,
  existing = null,
}: {
  projectId: string;
  onDone: () => void;
  /** Type choisi d'avance (« Nouvel entretien » → étude d'entretiens). */
  presetKind?: StudyKind | null;
  /** L'étude existe : seulement inviter (étapes 4 à 6). */
  existing?: { id: string; type: string; objective: string | null } | null;
}) {
  const { navigate } = useRouter();
  const qc = useQueryClient();
  const create = useCreateStudy(projectId);
  const startKind = existing ? studyKind(existing.type) : presetKind;
  const [step, setStep] = useState(existing ? 3 : presetKind ? 1 : 0);
  const [kind, setKind] = useState<StudyKind | null>(startKind);
  // Participants, créneaux, formats (étapes 4 et 5).
  const people = usePeople(projectId);
  const mailStatus = useMailStatus();
  // Une sélection faite dans l'annuaire arrive déjà cochée.
  const [personIds, setPersonIds] = useState<string[]>(() => takePresetPeople());
  const [slots, setSlots] = useState<string[]>([]);
  const [duration, setDuration] = useState(45);
  const [formats, setFormats] = useState<Format[]>(['google_meet']);
  const [location, setLocation] = useState('');
  const [subject, setSubject] = useState(startKind === 'test' ? 'Test d’une application' : 'Échange sur votre usage');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // Sans envoi d'e-mails, les liens personnels se montrent ICI, une fois.
  const [links, setLinks] = useState<{ studyId: string; result: BatchResult } | null>(null);

  // Cadrage
  const [objective, setObjective] = useState('');
  const [method, setMethod] = useState('');
  const [target, setTarget] = useState('');
  const [participants, setParticipants] = useState('');
  const [period, setPeriod] = useState('');
  const [context, setContext] = useState('');
  const [personaIds, setPersonaIds] = useState<string[]>([]);
  // Audit
  const [version, setVersion] = useState('');
  const [journey, setJourney] = useState('');
  const [auditDate, setAuditDate] = useState('');
  const [cats, setCats] = useState('');
  // Protocole
  const [protocol, setProtocol] = useState<StudyProtocol>(emptyProtocol());
  const [tasks, setTasks] = useState<string[]>(['']);

  const spec = kind ? KIND_BY_KEY[kind] : null;

  function choose(k: StudyKind) {
    if (k !== kind) {
      // Changer de type repart d'un protocole vierge : un guide d'entretien
      // n'a pas de sens pour un questionnaire. La méthode suit le type.
      setProtocol(emptyProtocol());
      setTasks(['']);
      setMethod(KIND_BY_KEY[k].methods[0] ?? '');
    }
    setKind(k);
    setSubject(k === 'test' ? 'Test d’une application' : 'Échange sur votre usage');
    setStep(1);
  }

  const canFrame = Boolean(existing) || (Boolean(kind) && objective.trim().length > 0);
  const sessions = hasSessions(kind);
  // Tant que le type n'est pas choisi, on montre tout le parcours.
  const lastStep = kind && !sessions ? 2 : 5;
  const slotsReady = slots.length > 0 && formats.length > 0 && (!formats.includes('onsite') || location.trim() !== '')
    && subject.trim() !== '';
  const inviting = personIds.length > 0 && slotsReady;
  const chosenPeople = (people.data?.people ?? []).filter((p) => personIds.includes(p.id));

  /** Les invitations, une fois l'étude créée (ou existante). */
  async function invite(studyId: string): Promise<BatchResult | null> {
    if (!inviting) return null;
    return api.post<BatchResult>(`/api/studies/${encodeURIComponent(studyId)}/invitations/batch`, {
      personIds, subject, message: message.trim() || null, slots, durationMin: duration,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      formats, location: formats.includes('onsite') ? location.trim() : null,
    });
  }

  /** La fin : aller sur l'étude, à l'onglet des participants s'il y a eu des invitations. */
  function finish(studyId: string, invited: boolean) {
    void qc.invalidateQueries({ queryKey: ['session-invitations', studyId] });
    void qc.invalidateQueries({ queryKey: ['people'] });
    if (invited) presetTab('etude', 'participants');
    onDone();
    navigate(`/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(studyId)}`);
  }

  /** Après l'envoi : les liens à transmettre soi-même, sinon direction l'étude. */
  function afterInvites(studyId: string, result: BatchResult | null) {
    if (result && result.sent.some((x) => !x.mailSent)) setLinks({ studyId, result });
    else finish(studyId, true);
  }

  /** Étude existante : seulement les invitations. */
  async function sendOnly() {
    if (!existing || !inviting) return;
    setSending(true);
    setSendError(null);
    try {
      afterInvites(existing.id, await invite(existing.id));
    } catch (e) {
      setSendError(e instanceof ApiRequestError ? e.message : 'L’envoi des invitations a échoué.');
    } finally {
      setSending(false);
    }
  }

  async function submit(e: FormEvent, { withInvites = true } = {}) {
    e.preventDefault();
    if (existing) { await sendOnly(); return; }
    if (!spec || !canFrame) return;
    // Pas encore au bout : le protocole mène aux participants.
    if (step < lastStep) { setStep(step + 1); return; }
    const n = Number.parseInt(participants, 10);
    const payload: NewStudy = {
      type: spec.label,
      method: method.trim() || undefined,
      objective: objective.trim(),
      target: target.trim() || undefined,
      period: period.trim() || undefined,
      context: context.trim() || undefined,
      participants: Number.isFinite(n) ? n : null,
      status: 'Planifiée',
    };
    if (spec.hasProtocol && protocolSize(protocol) > 0) payload.protocol = protocol;
    if (spec.kind === 'test') {
      payload.test = {
        type: method.trim() || undefined,
        personaIds,
        tasks: tasks.map((t) => t.trim()).filter(Boolean).map((action) => ({ action })),
      };
    }
    if (spec.kind === 'audit') {
      payload.audit = {
        version: version.trim() || undefined,
        userJourney: journey.trim() || undefined,
        auditDate: auditDate.trim() || undefined,
        analysisCats: cats.split(',').map((c) => c.trim()).filter(Boolean),
      };
    }
    setSendError(null);
    let studyId: string;
    try {
      studyId = (await create.mutateAsync(payload)).study.id;
    } catch {
      return; /* rendu par le bandeau */
    }
    // L'étude est créée ; une invitation ratée ne doit pas la faire recréer.
    let invited = false;
    if (withInvites && inviting) {
      setSending(true);
      try {
        const result = await invite(studyId);
        setSending(false);
        afterInvites(studyId, result);
        return;
      } catch (err) {
        setSendError(err instanceof ApiRequestError ? `Étude créée, mais les invitations n’ont pas pu partir : ${err.message}` : 'Étude créée, mais les invitations n’ont pas pu partir.');
        setSending(false);
        finish(studyId, false);
        return;
      }
      setSending(false);
    }
    finish(studyId, invited);
  }

  if (links) {
    const mailed = links.result.sent.filter((x) => x.mailSent).length;
    const byHand = links.result.sent.filter((x) => !x.mailSent);
    return (
      <div className="st-wizard st-session-step" role="status">
        <h2 className="t-h3">Invitations créées</h2>
        <p className="zy-texte-muet">
          {mailed ? `${mailed} invitation${mailed > 1 ? 's' : ''} envoyée${mailed > 1 ? 's' : ''} par e-mail. ` : ''}
          Envoyez vous-même leur lien personnel à ces personnes. <strong>Ces liens ne seront plus affichés ensuite.</strong>
        </p>
        <ul className="inv-links">
          {byHand.map((x) => {
            const link = `${window.location.origin}${hrefFor(`/rdv/${x.token}`)}`;
            return (
              <li key={x.personId}>
                <strong>{x.name}</strong>
                <input className="zy-input inv-link" readOnly value={link} aria-label={`Lien de ${x.name}`} onFocus={(e) => e.target.select()} />
                <Button variant="ghost" onClick={() => void navigator.clipboard.writeText(link)}>Copier</Button>
              </li>
            );
          })}
        </ul>
        {links.result.skipped.length ? (
          <p className="zy-texte-muet">{links.result.skipped.length} personne(s) non invitée(s) : {links.result.skipped.map((x) => x.reason).join(' ; ')}</p>
        ) : null}
        <div className="st-actions">
          <span />
          <Button variant="accent" onClick={() => finish(links.studyId, true)}>Aller au suivi des réservations</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="st-wizard">
      <ol className="zy-stepper" aria-label="Étapes de la nouvelle étude">
        {STEP_LABELS.map((label, i) => {
          if (i > lastStep || (existing && i < 3)) return null;
          const reachable = i === 0 || (i === 1 && kind) || (i >= 2 && canFrame) || (existing && i >= 3);
          return (
            <li key={label} {...(i < step ? { 'data-etat': 'fait' } : {})} {...(i === step ? { 'aria-current': 'step' as const } : {})}>
              <button type="button" className="st-step" disabled={!reachable} onClick={() => setStep(i)}>
                <span>{i === 2 && spec ? spec.protocolName : label}</span>
                {i === 0 && spec && step > 0 ? <span className="st-step-sub">{spec.label}</span> : null}
              </button>
            </li>
          );
        })}
      </ol>

      {step <= 1 && !existing ? <DemoQuota kind="studies" projectId={projectId} /> : null}
      {step === 0 ? (
        <fieldset className="st-kinds">
          <legend className="zy-field__label">Quel type d’étude voulez-vous mener ?</legend>
          {KINDS.map((k) => (
            <button
              key={k.kind}
              type="button"
              className={`st-kind${kind === k.kind ? ' st-kind-on' : ''}`}
              aria-pressed={kind === k.kind}
              onClick={() => choose(k.kind)}
            >
              <span className="st-kind-name">{k.label}</span>
              <span className="st-kind-purpose">{k.purpose}</span>
              <span className="st-kind-prepares">{k.prepares}</span>
            </button>
          ))}
        </fieldset>
      ) : null}

      {step === 1 && spec ? (
        <form
          className="write-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (canFrame) setStep(2);
          }}
        >
          <TextArea
            label="Objectif *"
            value={objective}
            onChange={setObjective}
            rows={3}
            placeholder={
              spec.kind === 'audit'
                ? 'Évaluer le parcours d’inventaire de la version mobile'
                : 'Comprendre pourquoi les chefs de chantier préparent encore leurs sorties sur papier'
            }
            hint="Ce que vous cherchez à comprendre ou à mesurer, pas ce que vous allez faire."
          />
          <Field
            label={spec.kind === 'audit' ? 'Référentiel' : 'Méthode'}
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            list={`st-methods-${spec.kind}`}
            hint="Suggestions ci-dessous, ou tapez la vôtre."
          />
          <datalist id={`st-methods-${spec.kind}`}>
            {spec.methods.map((m) => <option key={m} value={m} />)}
          </datalist>

          {spec.kind === 'audit' ? null : (
            <>
              {spec.kind === 'test' ? (
                <PersonaPicker projectId={projectId} selected={personaIds} onChange={setPersonaIds} />
              ) : null}
              <Field
                label={spec.kind === 'test' ? 'Cible (complément)' : 'Cible'}
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="Magasiniers des dépôts régionaux"
              />
              <div className="field-row">
                <Field
                  label={spec.participantsLabel}
                  type="number"
                  min={0}
                  value={participants}
                  onChange={(e) => setParticipants(e.target.value)}
                  hint={spec.participantsHint}
                />
                <Field label="Période" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Sem. 42–44" />
              </div>
            </>
          )}
          {spec.kind === 'audit' ? (
            <Field label="Période" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Octobre 2026" />
          ) : null}
          <TextArea
            label="Contexte"
            value={context}
            onChange={setContext}
            rows={3}
            hint="Ce qui a déclenché l’étude, les contraintes, ce qu’on sait déjà."
          />
          <div className="st-actions">
            <Button type="button" variant="ghost" onClick={() => setStep(0)}>Retour</Button>
            <Button type="submit" variant="accent" disabled={!canFrame}>
              Continuer : {spec.protocolThe}
            </Button>
          </div>
        </form>
      ) : null}

      {step === 2 && spec ? (
        <form className="write-form" noValidate onSubmit={submit}>
          {spec.hasProtocol ? (
            <ProtocolEditor
              projectId={projectId}
              spec={spec}
              value={protocol}
              onChange={setProtocol}
              study={{ objective, method, target, context }}
            />
          ) : spec.kind === 'test' ? (
            <>
              <p className="zy-texte-muet">
                Les tâches que les participants devront réaliser, dans l’ordre. Pendant les sessions,
                vous noterez pour chacune si elle est réussie, réalisée avec difficulté ou bloquée —
                sur la fiche du test, créée avec l’étude.
              </p>
              <TaskListEditor spec={spec} tasks={tasks} onChange={setTasks} />
            </>
          ) : (
            <>
              <div className="field-row">
                <Field label="Version auditée" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="v2 web" />
                <Field label="Date" type="date" value={auditDate} onChange={(e) => setAuditDate(e.target.value)} />
              </div>
              <Field label="Parcours audité" value={journey} onChange={(e) => setJourney(e.target.value)} placeholder="Préparation d’une sortie de chantier" />
              <Field
                label="Catégories d’analyse"
                value={cats}
                onChange={(e) => setCats(e.target.value)}
                placeholder="Navigation, Feedback, Charge cognitive"
                hint="Séparées par des virgules. La notation heuristique se fait ensuite sur la fiche de l’audit, créée avec l’étude."
              />
            </>
          )}
          {create.error ? (
            <ErrorBanner>{create.error instanceof ApiRequestError ? create.error.message : 'La création a échoué.'}</ErrorBanner>
          ) : null}
          <div className="st-actions">
            <Button type="button" variant="ghost" onClick={() => setStep(1)}>Retour</Button>
            <Button type="submit" variant="accent" loading={create.isPending} disabled={!canFrame}>
              {sessions ? 'Continuer : les participants' : 'Créer l’étude'}
            </Button>
          </div>
          {spec.hasProtocol && protocolSize(protocol) === 0 ? (
            <p className="zy-texte-muet st-later">
              Vous pourrez aussi préparer {spec.protocolThe} plus tard, depuis la fiche de l’étude.
            </p>
          ) : null}
        </form>
      ) : null}

      {step === 3 && sessions ? (
        <section className="st-session-step">
          <h2 className="t-h3">Qui inviter ?</h2>
          <p className="zy-texte-muet">
            Cochez les personnes dans l’annuaire. Filtrez par pool (vos groupes), persona, rôle, séniorité ou
            engagement, puis « Cocher les N affichées » pour prendre tout un groupe.
          </p>
          <PeoplePicker projectId={projectId} selected={personIds} onChange={setPersonIds} />
          <div className="st-actions">
            {existing ? <span /> : <Button type="button" variant="ghost" onClick={() => setStep(2)}>Retour</Button>}
            {/* L'annuaire se gère sur sa propre page ; continuer reste à côté. */}
            <div className="st-actions-group">
              <Link to={`/projet/${encodeURIComponent(projectId)}/personas`} className={buttonClass('accent')}>
                Gérer les utilisateurs
              </Link>
              <Button type="button" variant="accent" onClick={() => setStep(4)}>
                {personIds.length > 0 ? `Continuer : les créneaux (${personIds.length} personne${personIds.length > 1 ? 's' : ''})` : 'Continuer sans inviter'}
              </Button>
            </div>
          </div>
        </section>
      ) : null}

      {step === 4 && sessions ? (
        <section className="st-session-step">
          <h2 className="t-h3">Quand, et comment ?</h2>
          <div className="field-row">
            <label className="zy-field">
              <span className="zy-field__label">Durée d’une séance</span>
              <Select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
                {DURATIONS.map((d) => <option key={d} value={d}>{d} minutes</option>)}
              </Select>
            </label>
            <Field
              label="Sujet *"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              hint="Ce que les personnes liront. Pas votre hypothèse : elle orienterait leurs réponses."
            />
          </div>
          <div className="zy-field">
            <span className="zy-field__label">Créneaux proposés</span>
            <SlotPicker projectId={projectId} durationMin={duration} value={slots} onChange={setSlots} />
          </div>
          <div className="zy-field">
            <span className="zy-field__label">Formats proposés</span>
            <FormatPicker value={formats} onChange={setFormats} location={location} onLocation={setLocation} />
          </div>
          <InvitationMessage
            projectId={projectId}
            studyLabel={existing ? (existing.objective ?? '') : objective.trim()}
            durationMin={duration}
            formats={formats}
            location={location}
            value={message}
            onChange={setMessage}
          />
          <div className="st-actions">
            <Button type="button" variant="ghost" onClick={() => setStep(3)}>Retour</Button>
            <Button type="button" variant="accent" onClick={() => setStep(5)}>Continuer : le récapitulatif</Button>
          </div>
        </section>
      ) : null}

      {step === 5 && sessions ? (
        <form className="st-session-step" noValidate onSubmit={(e) => void submit(e)}>
          <h2 className="t-h3">Récapitulatif</h2>
          <dl className="facts st-recap">
            <div><dt>Étude</dt><dd>{existing ? (existing.objective ?? existing.type) : `${spec?.label ?? ''} — ${objective}`}</dd></div>
            {!existing && spec?.hasProtocol ? (
              <div><dt>{spec.protocolName}</dt><dd>{protocolSize(protocol) > 0 ? 'préparé' : 'à préparer plus tard'}</dd></div>
            ) : null}
            <div>
              <dt>Participants</dt>
              <dd>{chosenPeople.length ? chosenPeople.map(personName).join(', ') : 'personne pour l’instant'}</dd>
            </div>
            <div><dt>Créneaux</dt><dd>{slots.length} proposé{slots.length > 1 ? 's' : ''} · {duration} min</dd></div>
            <div><dt>Formats</dt><dd>{formats.map((f) => FORMAT_LABELS[f]).join(', ') || '—'}{formats.includes('onsite') && location ? ` (${location})` : ''}</dd></div>
          </dl>
          {personIds.length > 0 && !slotsReady ? (
            <Note tone="lecture">
              Pour inviter, il manque : {[
                slots.length === 0 ? 'au moins un créneau' : null,
                formats.length === 0 ? 'un format' : null,
                formats.includes('onsite') && !location.trim() ? 'le lieu' : null,
                !subject.trim() ? 'le sujet' : null,
              ].filter(Boolean).join(', ')}.
            </Note>
          ) : null}
          {mailStatus.data ? (
            mailStatus.data.configured ? (
              <p className="zy-texte-muet">
                ✉️ Les invitations partiront par e-mail depuis <strong>{mailStatus.data.from}</strong>, à votre nom ; les
                réponses vous reviennent. Vous serez prévenu de chaque réponse : acceptée, refusée, modifiée, annulée.
              </p>
            ) : (
              <Note tone="lecture">
                L’envoi d’e-mails n’est pas encore configuré (back-office › Clés › E-mail) : les liens s’afficheront
                pour que vous les transmettiez vous-même.
              </Note>
            )
          ) : null}
          <p className="zy-texte-muet">
            Chaque personne reçoit un lien personnel pour choisir son créneau et son format. Dès qu’elle réserve, sa
            fiche d’entretien est créée, et le suivi (invité, réservé, réalisé…) se lit dans l’onglet Participants.
          </p>
          {create.error ? (
            <ErrorBanner>{create.error instanceof ApiRequestError ? create.error.message : 'La création a échoué.'}</ErrorBanner>
          ) : null}
          {sendError ? <ErrorBanner>{sendError}</ErrorBanner> : null}
          <div className="st-actions">
            <Button type="button" variant="ghost" onClick={() => setStep(4)}>Retour</Button>
            <span className="st-final">
              {!existing ? (
                <Button
                  type="button"
                  variant="ghost"
                  loading={create.isPending && !inviting}
                  onClick={(e) => void submit(e as unknown as FormEvent, { withInvites: false })}
                >
                  Créer l’étude sans inviter
                </Button>
              ) : null}
              <Button type="submit" variant="accent" loading={create.isPending || sending} disabled={!inviting}>
                {existing
                  ? `Envoyer ${personIds.length} invitation${personIds.length > 1 ? 's' : ''}`
                  : `Créer l’étude et inviter ${personIds.length} personne${personIds.length > 1 ? 's' : ''}`}
              </Button>
            </span>
          </div>
        </form>
      ) : null}
    </div>
  );
}
